import {useCallback, useMemo} from 'react';
import {isNil, isNumber, sortBy} from 'lodash/fp';

import {BrokerMarketStatus, CarrierLevel, CompanyViewType} from 'modules/decisionsNext/api/types';
import {UseTreeData} from 'modules/decisionsNext/hooks/trees/types';

import {
  GenericTreeNode,
  groupSelectedKeys,
  groupSelectedKeysByLevel,
  hasChildren,
  iterateTree,
  transformTree,
  ungroupSelectedKeys,
} from 'utils/treeUtils';
import {
  transformMarketTreeWithUniqIds,
  getMarketInternalId,
  getMarketOriginalId,
  MarketEntityType,
} from 'modules/decisionsNext/utils/marketUtils';
import {useIsDownSm} from 'utils/hooks/useMediaQuery';
import {pluralize} from 'utils/formatters';
import {MarketTreeQuery, useMarketTreeQuery} from 'modules/decisionsNext/api/trees/marketTree.generated';
import {flattenTreeItems} from 'modules/decisionsNext/utils';
import {carrierLevelToCompanyView, intermediaryLevelToCompanyView} from 'core/constants/paths';
import {
  carrierLevelToFilterKey,
  carrierLevelToIntermediaryLevel,
  intermediaryLevelToFilterKey,
} from 'features/GlobalFilterProvider/utils';
import {MarketFilterKeys, intermediaryFilterKeys, carrierFilterKeys} from 'core/constants/policyFilter';
import {nullIfEmpty} from 'utils/arrayUtils';

import {selectedByLevelToFilter} from 'components/TreePicker/utils/filterTreePickerUtils';
import {StoreAdapterParams, TreeBasicNode} from 'components/Tree/TreeTypes';
import {useOptimizedSelection} from 'components/Tree/hooks/useOptimizedSelection';
import {extraCaseForMarketsSelectionCount} from 'components/form-next/TreeAutocomplete/components/TreePickerAutocomplete';

import {MarketPlacement} from 'modules/decisionsNext/components/BaseFilter/constants';

export type MarketNode = GenericTreeNode<
  TreeBasicNode & {
    companyStatus?: BrokerMarketStatus;
  }
>;

export const ALL_CARRIERS_ID = 'all-carriers';
export const ALL_INTERMEDIARIES_ID = 'all-intermediaries';

export const carriersRegex = new RegExp(
  `${CompanyViewType.UltimateParentCarrier}|${CompanyViewType.ParentCarrier}|${CompanyViewType.IssuingPaper}`
);

function getMarketTreeByCarrierLevel(data: MarketTreeQuery, carrierLevel: CarrierLevel): MarketNode[] {
  const flattenCarriers = flattenTreeItems(data.carriers.list);
  const flattenIntermediaries = flattenTreeItems(data.intermediaries.list);

  const carriersTreeItems: MarketNode[] = [];
  const intermediariesTreeItems: MarketNode[] = [];

  flattenCarriers.forEach(item => {
    if (item.availableViewModes.includes(carrierLevelToCompanyView[carrierLevel])) {
      carriersTreeItems.push({
        key: getMarketInternalId(item.id, carrierLevelToCompanyView[carrierLevel]),
        title: item.name,
      });
    }
  });

  flattenIntermediaries.forEach(item => {
    if (
      item.availableViewModes.includes(intermediaryLevelToCompanyView[carrierLevelToIntermediaryLevel[carrierLevel]])
    ) {
      intermediariesTreeItems.push({
        key: getMarketInternalId(
          item.id,
          intermediaryLevelToCompanyView[carrierLevelToIntermediaryLevel[carrierLevel]]
        ),
        title: item.name,
      });
    }
  });

  return [
    {
      key: ALL_CARRIERS_ID,
      title: 'Carriers',
      children: sortBy(item => item.title, carriersTreeItems),
    },
    {
      key: ALL_INTERMEDIARIES_ID,
      title: 'Intermediaries',
      children: sortBy(item => item.title, intermediariesTreeItems),
    },
  ];
}

export const useMarketTree: UseTreeData<
  string,
  MarketNode,
  {
    skip?: boolean;
    carrierLevel?: CarrierLevel;
  }
> = ({skip, carrierLevel} = {}) => {
  const {data, loading} = useMarketTreeQuery({
    skip,
    disableSkipSetsLoadingState: true,
  });

  const separatedTree = useMemo<MarketNode[]>(() => {
    if (carrierLevel && data) {
      return getMarketTreeByCarrierLevel(data, carrierLevel);
    }

    const carriersTreeFulfilled = transformMarketTreeWithUniqIds({
      items: data?.carriers.list ?? [],
      role: MarketEntityType.Carrier,
      transformId: (item, viewType) => {
        return getMarketInternalId(item.id, viewType);
      },
    });

    const intermediariesTreeFulfilled = transformMarketTreeWithUniqIds({
      items: data?.intermediaries.list ?? [],
      role: MarketEntityType.Intermediary,
      transformId: (item, viewType) => {
        return getMarketInternalId(item.id, viewType);
      },
    });

    return [
      {
        key: ALL_CARRIERS_ID,
        title: 'Carriers',
        children: transformTree(carriersTreeFulfilled, item => ({
          title: item.name,
          key: item.id,
          companyStatus: item.companyStatus,
        })),
      },
      {
        key: ALL_INTERMEDIARIES_ID,
        title: 'Intermediaries',
        children: transformTree(intermediariesTreeFulfilled, item => ({
          title: item.name,
          key: item.id,
          companyStatus: item.companyStatus,
        })),
      },
    ];
  }, [data, carrierLevel]);

  const treeMap = useMemo(() => {
    const map = new Map<string, TreeBasicNode & {companyStatus?: BrokerMarketStatus}>();

    iterateTree(
      separatedTree,
      (node, parent) => {
        map.set(node.key, {
          key: node.key,
          title: node.title,
          companyStatus: node.companyStatus,
          parentKey: parent?.key,
          childrenCount: hasChildren(node) ? node.children.length : undefined,
        });
      },
      {withParent: true}
    );

    return map;
  }, [separatedTree]);

  return {treeData: separatedTree, treeMap, loading};
};

export function splitMarketItems(selected: string[]) {
  return selected.reduce<{
    selectedCarriers: string[];
    selectedIntermediaries: string[];
  }>(
    (acc, marketId) => {
      const isCarrier = marketId.search(carriersRegex);

      if (isCarrier !== -1) {
        return {...acc, selectedCarriers: [...acc.selectedCarriers, marketId]};
      } else {
        return {...acc, selectedIntermediaries: [...acc.selectedIntermediaries, marketId]};
      }
    },
    {
      selectedCarriers: [],
      selectedIntermediaries: [],
    }
  );
}

const carrierViewTypes = [
  CompanyViewType.UltimateParentCarrier,
  CompanyViewType.ParentCarrier,
  CompanyViewType.IssuingPaper,
];
const intermediaryViewTypes = [
  CompanyViewType.UltimateParentIntermediary,
  CompanyViewType.ParentIntermediary,
  CompanyViewType.Intermediary,
];

export const getSelectedCarriersByLevelWithOriginalIds = (
  newSelected: string[],
  treeData: MarketNode[],
  children: GenericTreeNode<TreeBasicNode>[]
) => {
  const groupedSelectedCarriers = groupSelectedKeys(children, newSelected);
  const selectedCarriersByLevel = groupSelectedKeysByLevel(treeData, groupedSelectedCarriers, -1);
  return selectedCarriersByLevel.map(selected => selected.map(getMarketOriginalId));
};

export const getMarketTreeShortSelectionCount = (selected: string[]) => {
  const {selectedCarriers, selectedIntermediaries} = splitMarketItems(selected);

  return extraCaseForMarketsSelectionCount(selectedCarriers.length, selectedIntermediaries.length);
};

export const getMarketTreeShortLocalSelectedLabel = (selected: string[]) =>
  `Selected ${getMarketTreeShortSelectionCount(selected)}`;

/**
 * TODO: Refactor to separate carrierLevel logic, because it's not included in new hook
 * @deprecated - use `useMarketTreeSelection` from `filters/features/FilterDialog/hooks/tree-selections/useMarketTreeSelection.ts`
 */
export function useMarketTreeSelection(
  treeData: MarketNode[],
  {store, setStore}: StoreAdapterParams<MarketFilterKeys | 'direct' | 'brokerMarketStatuses'>,
  carrierLevel?: CarrierLevel
) {
  const {
    ultimateCarrierIds,
    parentCarrierIds,
    issuingPaperIds,
    ultimateIntermediaryIds,
    parentIntermediaryIds,
    childIntermediaryIds,
    direct,
    brokerMarketStatuses,
  } = store;

  const intermediaryLevel = carrierLevel ? carrierLevelToIntermediaryLevel[carrierLevel] : undefined;

  const carrierTreeData = useMemo(() => {
    return treeData.find(item => item.key === ALL_CARRIERS_ID)?.children ?? [];
  }, [treeData]);
  const intermediaryTreeData = useMemo(() => {
    return treeData.find(item => item.key === ALL_INTERMEDIARIES_ID)?.children ?? [];
  }, [treeData]);

  const selectedCarriers = useMemo(() => {
    if (brokerMarketStatuses?.length) {
      const selectedCarriersByStatus = carrierTreeData
        .filter(carrier => !isNil(carrier?.companyStatus) && brokerMarketStatuses.includes(carrier.companyStatus))
        .map(item => item.key);

      return Array.from(ungroupSelectedKeys(treeData, selectedCarriersByStatus));
    }

    const allIds = [ultimateCarrierIds, parentCarrierIds, issuingPaperIds].flatMap(
      (group, index) => group?.map(id => getMarketInternalId(id, carrierViewTypes[index] || carrierViewTypes[0])) || []
    );
    return Array.from(ungroupSelectedKeys(treeData, allIds));
  }, [brokerMarketStatuses, carrierTreeData, issuingPaperIds, parentCarrierIds, treeData, ultimateCarrierIds]);

  const selectedIntermediaries = useMemo(() => {
    if (brokerMarketStatuses?.length) {
      const selectedIntermediariesByStatus = intermediaryTreeData
        .filter(
          intermediary =>
            !isNil(intermediary?.companyStatus) && brokerMarketStatuses.includes(intermediary.companyStatus)
        )
        .map(item => item.key);

      return Array.from(ungroupSelectedKeys(treeData, selectedIntermediariesByStatus));
    }

    const allIds = [ultimateIntermediaryIds, parentIntermediaryIds, childIntermediaryIds].flatMap(
      (group, index) => group?.map(id => getMarketInternalId(id, intermediaryViewTypes[index])) || []
    );
    return Array.from(ungroupSelectedKeys(treeData, allIds));
  }, [
    brokerMarketStatuses,
    childIntermediaryIds,
    intermediaryTreeData,
    parentIntermediaryIds,
    treeData,
    ultimateIntermediaryIds,
  ]);

  const setSelectedNoneCarriers = useCallback(() => {
    setStore({
      ultimateCarrierIds: undefined,
      parentCarrierIds: undefined,
      issuingPaperIds: undefined,
    });
  }, [setStore]);

  const carrierSelection = useOptimizedSelection({
    setSelected: useCallback(
      (newSelected: string[]) => {
        const {selectedCarriers} = splitMarketItems(newSelected);
        const selectedCarriersByLevelWithOriginalIds = getSelectedCarriersByLevelWithOriginalIds(
          selectedCarriers,
          treeData,
          carrierTreeData
        );

        setStore(
          carrierLevel
            ? {[carrierLevelToFilterKey[carrierLevel]]: selectedCarriersByLevelWithOriginalIds[0]}
            : selectedByLevelToFilter(selectedCarriersByLevelWithOriginalIds, carrierFilterKeys)
        );
      },
      [carrierTreeData, setStore, treeData, carrierLevel]
    ),
    selected: selectedCarriers,
    setSelectedNone: setSelectedNoneCarriers,
    treeData: carrierTreeData,
  });

  const setSelectedNoneIntermediaries = useCallback(() => {
    setStore({
      ultimateIntermediaryIds: undefined,
      parentIntermediaryIds: undefined,
      childIntermediaryIds: undefined,
    });
  }, [setStore]);

  const disabled = useMemo(() => direct === true, [direct]);

  const intermediarySelection = useOptimizedSelection({
    setSelected: useCallback(
      (newSelected: string[]) => {
        const {selectedIntermediaries} = splitMarketItems(newSelected);
        const selectedIntermediariesByLevelWithOriginalIds = getSelectedCarriersByLevelWithOriginalIds(
          selectedIntermediaries,
          treeData,
          intermediaryTreeData
        );

        setStore(
          intermediaryLevel
            ? {
                [intermediaryLevelToFilterKey[intermediaryLevel]]: selectedIntermediariesByLevelWithOriginalIds[0],
              }
            : selectedByLevelToFilter(selectedIntermediariesByLevelWithOriginalIds, intermediaryFilterKeys)
        );
      },
      [intermediaryTreeData, setStore, treeData, intermediaryLevel]
    ),
    selected: selectedIntermediaries,
    setSelectedNone: setSelectedNoneIntermediaries,
    treeData: intermediaryTreeData,
    disabled,
  });

  const selected = useMemo(() => {
    return [...carrierSelection.selected, ...intermediarySelection.selected];
  }, [carrierSelection.selected, intermediarySelection.selected]);

  const setSelected = useCallback(
    (newSelected: string[]) => {
      const {selectedCarriers, selectedIntermediaries} = splitMarketItems(newSelected);

      carrierSelection.setSelected(selectedCarriers);
      intermediarySelection.setSelected(selectedIntermediaries);

      setStore({
        brokerMarketStatuses: undefined,
      });
    },
    [carrierSelection, intermediarySelection, setStore]
  );

  const isAllCarriersSelected = carrierSelection.selectionCount === 'All';
  const isAllIntermediariesSelected = intermediarySelection.selectionCount === 'All';

  const setPlacementValue = useCallback(
    (placementValue: MarketPlacement) => {
      const newDirect = placementValue === MarketPlacement.All ? undefined : placementValue === MarketPlacement.Direct;

      setStore({direct: newDirect});
    },
    [setStore]
  );

  const setBrokerMarketStatuses = useCallback(
    (statuses: BrokerMarketStatus[]) => {
      carrierSelection.setSelected([]);
      intermediarySelection.setSelected([]);

      setStore({
        brokerMarketStatuses: nullIfEmpty(statuses),
      });
    },
    [setStore, carrierSelection, intermediarySelection]
  );

  const isDownSm = useIsDownSm();

  const selectionCount = useMemo(() => {
    const carriersCount = carrierSelection.selectionCount;
    const intermediariesCount = intermediarySelection.selectionCount;

    if (isDownSm) {
      // idea is to return only number of selected items for mobile view or All. Since there is not enough space for words
      // carriersCount or intermediariesCount could be equal to 'All'
      return (Number(carriersCount) || 0) + (Number(intermediariesCount) || 0) || 'All';
    }

    if (direct === true) {
      return isAllCarriersSelected
        ? 'All policies placed directly with a carrier'
        : `${carriersCount} 'Direct' to Carrier`;
    }

    if (direct === false && isAllIntermediariesSelected) {
      const carriers = isNumber(carriersCount)
        ? `${carriersCount} ${pluralize(carriersCount, 'carrier', 'carriers')}`
        : 'carriers';

      return `All policies placed indirectly with ${carriers}`;
    }

    return isAllCarriersSelected && isAllIntermediariesSelected
      ? 'All Markets'
      : `Carriers: ${carrierSelection.selectionCount}. Intermediaries: ${intermediarySelection.selectionCount}`;
  }, [
    carrierSelection.selectionCount,
    direct,
    intermediarySelection.selectionCount,
    isAllCarriersSelected,
    isAllIntermediariesSelected,
    isDownSm,
  ]);

  const shortSelectionCount = useMemo(() => {
    return extraCaseForMarketsSelectionCount(carrierSelection.selectionCount, intermediarySelection.selectionCount);
  }, [carrierSelection.selectionCount, intermediarySelection.selectionCount]);

  const placementValue = useMemo(
    () => (isNil(direct) ? MarketPlacement.All : direct ? MarketPlacement.Direct : MarketPlacement.Indirect),
    [direct]
  );

  const brokerMarketStatusesValues = useMemo(
    () => (isNil(brokerMarketStatuses) ? [] : brokerMarketStatuses),
    [brokerMarketStatuses]
  );

  const disabledKeys = useMemo(
    () => [...carrierSelection.disabledKeys, ...intermediarySelection.disabledKeys],
    [carrierSelection.disabledKeys, intermediarySelection.disabledKeys]
  );

  return {
    selected,
    setSelected,
    setCarriersSelected: carrierSelection.setSelected,
    setCarriersSelectedNone: carrierSelection.setSelectedNone,
    setIntermediariesSelected: intermediarySelection.setSelected,
    setIntermediariesSelectedNone: intermediarySelection.setSelectedNone,
    selectionCountState: {
      carriersCount: carrierSelection.selectionCount,
      intermediaryCount: intermediarySelection.selectionCount,
      countMessage: selectionCount,
      shortCountMessage: shortSelectionCount,
    },
    getShortLocalSelectedLabel: getMarketTreeShortLocalSelectedLabel,
    placementValue,
    selectedNoneCarriers: {
      ultimateCarrierIds: undefined,
      parentCarrierIds: undefined,
      issuingPaperIds: undefined,
    },
    selectedNoneIntermediaries: {
      ultimateIntermediaryIds: undefined,
      parentIntermediaryIds: undefined,
      childIntermediaryIds: undefined,
    },
    selectedNonePlacement: {
      direct: undefined,
    },
    selectedNoneBrokerMarketStatuses: {
      brokerMarketStatuses: undefined,
    },
    setPlacementValue,
    disabledKeys,
    setBrokerMarketStatuses,
    brokerMarketStatuses: brokerMarketStatusesValues,
    hasStrategicCarriers:
      intermediaryTreeData.some(intermediary =>
        [BrokerMarketStatus.Strategic, BrokerMarketStatus.NonStrategic].includes(
          intermediary.companyStatus as BrokerMarketStatus
        )
      ) ||
      carrierTreeData.some(carrier =>
        [BrokerMarketStatus.Strategic, BrokerMarketStatus.NonStrategic].includes(
          carrier.companyStatus as BrokerMarketStatus
        )
      ),
  };
}
